<template>
  <PainelLayout>
    <Breadcrumb :lista="[{ 'name':'Painel', 'link' : '/' }]" />
    <h1>Movimentações</h1>
    <hr/>

    <!-- Filtro por período -->
    <div class="row g-2 align-items-end mb-2">
      <div class="col-auto">
        <div class="btn-group" role="group" aria-label="Atalhos de período">
          <button
            v-for="preset in presets" :key="preset.key"
            type="button"
            class="btn btn-sm"
            :class="activePreset === preset.key ? 'btn-primary' : 'btn-outline-secondary'"
            @click="selectPreset(preset.key)"
          >{{ preset.label }}</button>
        </div>
      </div>
    </div>

    <div class="row g-2 align-items-end mb-3">
      <div class="col-auto">
        <label class="form-label">De</label>
        <input type="date" class="form-control" v-model="filters.date_start" :max="filters.date_end" @change="onFilterChange">
      </div>
      <div class="col-auto">
        <label class="form-label">Até</label>
        <input type="date" class="form-control" v-model="filters.date_end" :min="filters.date_start" @change="onFilterChange">
      </div>
      <div class="col-auto">
        <button class="btn btn-outline-secondary" @click="resetFilters">Limpar</button>
      </div>
    </div>

    <app-table empty-message="Nenhuma movimentação encontrada." :empty="!loading && lista.length === 0"
      :colunas="[{ label: 'Tipo' }, { label: 'Locação' }, { label: 'Descrição' }, { label: 'Data' }, { label: 'Usuário' }, { label: '' }]">
      <app-trow
        v-for="item in lista" :key="item.id"
        :data="[
          { value: statusFormat.type(item.type) },
          { value: item.rental?.code ?? '—' },
          { value: item.description },
          { value: dateFormat.dateTime(item.created_at) },
          { value: item.user?.name ?? '—' }
        ]"
      >
        <template #actions>
          <router-link
            v-if="item.rental?.id"
            class="btn btn-sm btn-primary"
            :to="{ path: '/rental/details', query: { id: item.rental.id } }"
          >
            <i class="bi bi-file-earmark-text me-2"></i>Ver locação
          </router-link>
        </template>
      </app-trow>
    </app-table>

    <div v-if="loading" class="d-flex justify-content-center p-4">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <!-- Paginação -->
    <div v-if="!loading && total > 0" class="d-flex justify-content-between align-items-center mt-3">
      <span class="text-secondary" style="font-size: 13px;">
        {{ from }}–{{ to }} de {{ total }}
      </span>
      <div class="btn-group">
        <button class="btn btn-outline-secondary btn-sm" :disabled="page <= 1" @click="goToPage(page - 1)">
          &laquo; Anterior
        </button>
        <button class="btn btn-outline-secondary btn-sm disabled">
          Página {{ page }} de {{ lastPage }}
        </button>
        <button class="btn btn-outline-secondary btn-sm" :disabled="page >= lastPage" @click="goToPage(page + 1)">
          Próxima &raquo;
        </button>
      </div>
    </div>

    <div class="margin-footer"></div>
  </PainelLayout>
</template>

<script>
import PainelLayout from '@/layouts/PainelLayout.vue';
import Breadcrumb from '@/components/itens/breadcrumb.vue';
import AppTable from '@/components/AppTable.vue';
import AppTrow from '@/components/AppTrow.vue';
import { onMounted, reactive, ref } from 'vue';
import { movements } from '@/models/movements';
import statusFormat from '@/services/statusFormat';
import dateFormat from '@/services/dateFormat';
import { useToastStore } from '@/stores/toastStore';

function toLocalISODate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function daysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d;
}

export default {
  name: "MovementsView",
  components: { PainelLayout, Breadcrumb, AppTable, AppTrow },
  setup() {
    const toastStore = useToastStore();
    const model = movements();

    const loading = ref(true);
    const lista = ref([]);
    const page = ref(1);
    const lastPage = ref(1);
    const total = ref(0);
    const from = ref(0);
    const to = ref(0);
    const activePreset = ref(null);

    const filters = reactive({
      date_start: '',
      date_end: ''
    });

    const presets = [
      { key: 'today', label: 'Hoje' },
      { key: 'yesterday', label: 'Ontem' },
      { key: '7d', label: '7 dias' },
      { key: '30d', label: '30 dias' },
    ];

    function rangeForPreset(key) {
      switch (key) {
        case 'today': return [daysAgo(0), daysAgo(0)];
        case 'yesterday': return [daysAgo(1), daysAgo(1)];
        case '7d': return [daysAgo(6), daysAgo(0)];
        case '30d': return [daysAgo(29), daysAgo(0)];
        default: return [daysAgo(0), daysAgo(0)];
      }
    }

    function selectPreset(key) {
      activePreset.value = key;
      const [start, end] = rangeForPreset(key);
      filters.date_start = toLocalISODate(start);
      filters.date_end = toLocalISODate(end);
      page.value = 1;
      fetchList();
    }

    async function fetchList() {
      loading.value = true;
      try {
        const response = await model.list({
          date_start: filters.date_start || undefined,
          date_end: filters.date_end || undefined,
          page: page.value
        });
        lista.value = response.data;
        page.value = response.current_page;
        lastPage.value = response.last_page;
        total.value = response.total;
        from.value = response.from ?? 0;
        to.value = response.to ?? 0;
      } catch (error) {
        toastStore.error('Não foi possível carregar as movimentações.');
      } finally {
        loading.value = false;
      }
    }

    function onFilterChange() {
      activePreset.value = null; // datas editadas à mão deixam de refletir um atalho
      page.value = 1;
      fetchList();
    }

    function resetFilters() {
      activePreset.value = null;
      filters.date_start = '';
      filters.date_end = '';
      page.value = 1;
      fetchList();
    }

    function goToPage(p) {
      if (p < 1 || p > lastPage.value) return;
      page.value = p;
      fetchList();
    }

    onMounted(() => {
      fetchList();
    });

    return {
      loading, lista, filters, page, lastPage, total, from, to,
      presets, activePreset, selectPreset,
      onFilterChange, resetFilters, goToPage,
      statusFormat, dateFormat
    };
  }
}
</script>

<style scoped>
  .margin-footer {
    height: 20px;
  }
</style>
